import { useEffect, useState } from "react";
import { Copy, Hash, Lock, Mail, Star, UserPlus, Headphones } from "lucide-react";
import { toast } from "sonner";
import dayjs from "dayjs";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Avatar, Presence } from "./Avatar";
import { PeoplePicker } from "./PeoplePicker";
import { useChat } from "@/lib/chat";
import { useAuth } from "@/lib/auth";
import { api, errMsg } from "@/lib/api";
import { copyText } from "@/lib/format";
import { analytics } from "@/lib/analytics";

const EditableRow = ({ label, value, placeholder, onSave, testid, multiline }) => {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value || "");
  useEffect(() => setV(value || ""), [value]);
  if (editing) {
    return (
      <div className="tc-detail-row" style={{ cursor: "default" }}>
        <div className="k">{label}</div>
        {multiline ? <textarea className="tc-textarea" value={v} onChange={(e) => setV(e.target.value)} autoFocus data-testid={`${testid}-input`} /> : <input className="tc-input sm" value={v} onChange={(e) => setV(e.target.value)} autoFocus data-testid={`${testid}-input`} />}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
          <button className="tc-btn tc-btn--outline sm" onClick={() => setEditing(false)}>Cancel</button>
          <button className="tc-btn tc-btn--primary sm" onClick={async () => { await onSave(v); setEditing(false); }} data-testid={`${testid}-save`}>Save</button>
        </div>
      </div>
    );
  }
  return (
    <button className="tc-detail-row" onClick={() => setEditing(true)} data-testid={testid}>
      <div className="k">{label}<span className="edit">Edit</span></div>
      <div className={`v${value ? " is-set" : ""}`}>{value || placeholder}</div>
    </button>
  );
};

export const ChannelDetails = ({ conversationId, initialTab = "about", onClose }) => {
  const { conversations, updateConversation, addMembers, leaveChannel, toggleStar, openProfile, openDm, joinHuddle, can } = useChat();
  const { workspace } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [detail, setDetail] = useState(null);
  const [adding, setAdding] = useState(false);
  const [sel, setSel] = useState([]);
  const c = { ...(conversations.find((x) => x.id === conversationId) || {}), ...(detail || {}) };
  const reload = () => api.get(`/conversations/${conversationId}`).then((r) => setDetail(r.data)).catch(() => {});
  useEffect(() => { reload(); }, [conversationId]); // eslint-disable-line react-hooks/exhaustive-deps
  const isChannel = c.type === "channel";
  const save = (field) => async (v) => { try { await updateConversation(conversationId, { [field]: v }); reload(); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); } };
  const copy = (v) => { copyText(v); analytics.track("email_copied", { category: "email", target: "conversation", target_id: conversationId, label: v }); toast.success("Email address copied"); };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="tc-dialog max-w-[560px] p-6" data-testid="channel-details-modal">
        <DialogTitle asChild>
          <h2 style={{ display: "flex", alignItems: "center", gap: 6 }}>
            {isChannel ? (c.is_private ? <Lock size={18} /> : <Hash size={20} strokeWidth={2.6} />) : null}{c.display_name}
          </h2>
        </DialogTitle>
        <DialogDescription className="sr-only">Conversation details</DialogDescription>
        {isChannel && (
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button className="tc-btn tc-btn--outline sm" onClick={() => toggleStar(conversationId)} data-testid="details-star-button"><Star size={14} fill={c.is_starred ? "currentColor" : "none"} /> {c.is_starred ? "Starred" : "Star"}</button>
            <button className="tc-btn tc-btn--outline sm" onClick={() => { joinHuddle(conversationId); onClose(); }} data-testid="details-huddle-button"><Headphones size={14} /> Huddle</button>
          </div>
        )}
        <div className="tc-tabsbar">
          {["about", "members", "integrations", "settings"].filter((t) => isChannel || t !== "settings").map((t) => (
            <button key={t} className={tab === t ? "is-active" : ""} onClick={() => setTab(t)} data-testid={`details-tab-${t}`}>{t[0].toUpperCase() + t.slice(1)}{t === "members" ? ` ${c.member_count || ""}` : ""}</button>
          ))}
        </div>
        {tab === "about" && (
          <div>
            <div className="tc-detail-block">
              {isChannel && <EditableRow label="Topic" value={c.topic} placeholder="Add a topic" onSave={save("topic")} testid="details-topic" />}
              {isChannel && <EditableRow label="Description" value={c.description} placeholder="Add a description" onSave={save("description")} testid="details-description" multiline />}
              <div className="tc-detail-row" style={{ cursor: "default" }}>
                <div className="k">Created by</div>
                <div className="v is-set">{c.created_by_info?.display_name || "—"} on {c.created_at ? dayjs(c.created_at).format("MMMM D, YYYY") : "—"}</div>
              </div>
            </div>
            {isChannel && (
              <div className="tc-detail-block">
                <div className="tc-detail-row" style={{ cursor: "default" }}>
                  <div className="k"><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Mail size={15} /> Channel email address</span></div>
                  <div className="v">Send emails to this channel. Messages will be posted by Total Chat.</div>
                  <div className="tc-email-box" data-testid="details-channel-email"><code>{c.email}</code><button className="tc-copybtn" onClick={() => copy(c.email)} title="Copy" data-testid="details-copy-email"><Copy /></button></div>
                </div>
              </div>
            )}
            {isChannel && !c.is_default && (
              <button className="tc-btn tc-btn--danger" style={{ marginTop: 16, width: "100%" }} onClick={() => { leaveChannel(conversationId); onClose(); }} data-testid="details-leave-button">Leave channel</button>
            )}
          </div>
        )}
        {tab === "members" && (
          <div style={{ marginTop: 12 }}>
            {adding ? (
              <div>
                <PeoplePicker selected={sel} onChange={setSel} exclude={c.members || []} />
                <div className="tc-dialog__footer">
                  <button className="tc-btn tc-btn--outline" onClick={() => setAdding(false)}>Cancel</button>
                  <button className="tc-btn tc-btn--primary" disabled={!sel.length} onClick={async () => { try { await addMembers(conversationId, sel); setSel([]); setAdding(false); reload(); toast.success("People added"); } catch (e) { toast.error(errMsg(e)); } }} data-testid="details-add-people-confirm">Add</button>
                </div>
              </div>
            ) : (
              <>
                {(!isChannel || can("channels.add_members")) && <button className="tc-people-row" onClick={() => setAdding(true)} data-testid="details-add-people"><span className="tc-avatar" style={{ width: 24, height: 24, borderRadius: 5, background: "var(--tc-selected)", color: "var(--tc-link)" }}><UserPlus size={14} /></span><span style={{ color: "var(--tc-link)", fontWeight: 700 }}>Add people</span></button>}
                <div className="tc-people-list tc-scroll" style={{ maxHeight: 360 }}>
                  {(c.members_info || []).map((u) => (
                    <button key={u.id} className="tc-people-row" onClick={() => { openProfile(u.id); onClose(); }} data-testid={`details-member-${u.handle}`}>
                      <Avatar user={u} size={24} radius={5} /><span>{u.display_name}</span><Presence presence={u.presence} /><span className="sub">{u.title}</span><span className="right">{u.chat_email}</span>
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
        {tab === "integrations" && (
          <div style={{ marginTop: 12, fontSize: 15 }}>
            <div className="tc-detail-block">
              <div className="tc-detail-row" style={{ cursor: "default" }}>
                <div className="k"><span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><Mail size={15} /> {isChannel ? "Send emails to this channel" : "Email addresses"}</span></div>
                {isChannel ? (
                  <>
                    <div className="v">Forward emails to this address and they'll appear here as messages. Point your mail provider at the Total Chat inbound API (see Admin → API docs).</div>
                    <div className="tc-email-box"><code>{c.email}</code><button className="tc-copybtn" onClick={() => copy(c.email)} title="Copy"><Copy /></button></div>
                  </>
                ) : (
                  (c.members_info || []).map((u) => <div key={u.id} className="tc-email-box"><Avatar user={u} size={20} radius={4} /><code>{u.chat_email}</code><button className="tc-copybtn" onClick={() => copy(u.chat_email)} title="Copy"><Copy /></button></div>)
                )}
              </div>
              <div className="tc-detail-row" style={{ cursor: "default" }}>
                <div className="k">Apps & API</div>
                <div className="v">Other tools can post into this conversation with <code className="tc-code">POST /api/v1/conversations/{conversationId}/messages</code> using an API key. Domain: {workspace?.email_domain}</div>
              </div>
            </div>
          </div>
        )}
        {tab === "settings" && isChannel && (
          <div style={{ marginTop: 12 }}>
            <div className="tc-detail-block">
              <EditableRow label="Channel name" value={c.name} placeholder="channel-name" onSave={save("name")} testid="details-name" />
              <div className="tc-detail-row" style={{ cursor: "default" }}>
                <div className="k">Visibility</div>
                <div className="v is-set">{c.is_private ? "Private — only invited people can find and join" : "Public — anyone in the workspace can join"}</div>
              </div>
            </div>
            {!c.is_default && can("channels.archive") && (
              <button className="tc-btn tc-btn--outline" style={{ marginTop: 16, width: "100%" }} data-testid="details-archive-button"
                onClick={async () => { if (!window.confirm(`Archive #${c.name}? It will disappear from everyone's sidebar. Messages are kept and the channel can be un-archived by an admin.`)) return; try { await updateConversation(conversationId, { archived: true }); analytics.track("channel_archived", { category: "channels", target: "conversation", target_id: conversationId }); toast.success(`#${c.name} archived`); onClose(); } catch (e) { toast.error(errMsg(e)); } }}>Archive channel</button>
            )}
            {!c.is_default && <button className="tc-btn tc-btn--danger" style={{ marginTop: 16, width: "100%" }} onClick={() => { leaveChannel(conversationId); onClose(); }}>Leave channel</button>}
          </div>
        )}
        {!isChannel && tab === "about" && (
          <div style={{ marginTop: 4 }}>
            {(c.members_info || []).map((u) => (
              <button key={u.id} className="tc-people-row" onClick={() => { openProfile(u.id); onClose(); }}><Avatar user={u} size={24} radius={5} /><span>{u.display_name}</span><Presence presence={u.presence} /><span className="right">{u.chat_email}</span></button>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
